import { all, call, delay, put, takeLatest } from 'redux-saga/effects';
import {
    leaveCommentSuccess,
    leaveCommentFail,
    updateCommentSuccess,
    updateCommentFail,
    deleteCommentSuccess,
    deleteCommentFail,
    likeCommentSuccess,
    likeCommentFail,
    deleteCommentLikeSuccess,
    deleteCommentLikeFail,
    getCommentSuccess,
    getCommentFail,
} from './actions';
import { globalErrorHandler } from '../error/saga';
import { api } from '../../configurations/api';
import { commentUrl, getProjectcommentUrl, likeUrl } from '../../configurations/api/url';
import { httpRequest } from '../types';
import { IGenericDocumentModifier } from '../../types/global/helper';
import { IGetComments, ILeaveComment, IReactToComment, types } from './types';
import { Action } from 'redux';
import { CACHE_TYPE, NotUniqueCacheValue } from '../cache/types';
import { getNotUniqueCacheByKey, isUseCacheEnabled } from '../cache/saga';
import { clearCache, destroyOneCache, setCache } from '../cache/action';
import { SentryCapture } from '../../analytics/Sentry';
import { refreshUserDashboard } from '../account/actions';
import dayjs from 'dayjs';

function* likeAComment({ payload }: { payload: IReactToComment }): any {
    try {
        const { like_type, commentId, like_category } = payload;
        const request = `${commentUrl}/${commentId}/like`;
        const response = yield call(api, request, httpRequest.POST, { like_category, like_type }, 0, 0, false);
        const { data } = response.data;
        yield put(likeCommentSuccess({ data, documentId: commentId }));
        // TODO: Attempt to clear just the cache of the specified like.
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.USER_COMMENT_LIKES,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(likeCommentFail({ ...error, documentId: payload.commentId }));
    }
}

function* deleteCommentLike({ payload }: { payload: IGenericDocumentModifier }): any {
    try {
        const { documentId } = payload;
        const request = `${likeUrl}/${documentId}`;
        yield call(api, request, httpRequest.DELETE, {}, 2, 2000, false);
        yield put(deleteCommentLikeSuccess(payload));
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.USER_COMMENT_LIKES,
            }),
        );
        yield put(
            destroyOneCache({
                cacheType: CACHE_TYPE.DISCOVERY,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteCommentLikeFail({ ...error, documentId: payload.additionalPayload._id }));
    }
}

function* getCommentsAction({ payload }: { payload: IGetComments }): any {
    const defaultUseCache = yield* isUseCacheEnabled();
    let initialResult: any = null;

    const queryParams = new URLSearchParams();
    // Conditionally add parameters
    if (payload.limit) queryParams.append('limit', String(payload.limit));
    if (payload.page) queryParams.append('page', String(payload.page));
    if (payload.category) queryParams.append('category', payload.category);

    const cache: NotUniqueCacheValue = yield* getNotUniqueCacheByKey(
        CACHE_TYPE.COMMENTS,
        queryParams.toString() as string,
    );
    if (cache && cache.key) {
        initialResult = cache.value;
    }
    if (initialResult && defaultUseCache) {
        yield put(getCommentSuccess(initialResult));
    } else {
        try {
            const response = yield call(
                api,
                `${getProjectcommentUrl}${payload.params}`,
                httpRequest.GET,
                {},
                2,
                2000,
                false,
            );
            yield put(getCommentSuccess(response));
            yield put(
                setCache({
                    key: queryParams.toString(),
                    value: response,
                    type: CACHE_TYPE.COMMENTS,
                    isUnique: false,
                }),
            );
        } catch (error: any) {
            SentryCapture(error, 'error');
            yield put(getCommentFail(error));
            yield call(globalErrorHandler, error);
        }
    }
}

function* leaveAComment({ payload }: { payload: ILeaveComment }): any {
    const { comment, id, isReply, parentComment, commentType } = payload || {};
    let request;
    let body: any = {
        comment,
    };
    if (isReply) {
        request = `${commentUrl}/${parentComment?._id}/reply`;
    } else {
        request = `${commentUrl}`;
        body = {
            [commentType]: id,
            comment,
        };
    }
    try {
        const response = yield call(api, request, httpRequest.POST, { ...body, created_at: dayjs() }, 2, 2000);
        const { data } = response.data;
        yield put(
            leaveCommentSuccess({
                comment: data,
                isReply: payload.isReply,
                parentComment: payload.parentComment,
                commentOrigin: payload.commentOrigin,
            }),
        );
        yield put(
            clearCache({
                cacheType: CACHE_TYPE.COMMENTS,
                key: `?${commentType}=${id}`,
            }),
        );
        yield delay(1000);
        yield put(refreshUserDashboard());
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(leaveCommentFail(error));
    }
}

function* updateComment({ payload }: { payload: IGenericDocumentModifier }): any {
    const url = `${commentUrl}/${payload.documentId}`;
    try {
        const response = yield call(api, url, httpRequest.PATCH, { comment: payload.payload }, 2, 2000);
        const { data } = response.data;
        yield put(updateCommentSuccess({ documentId: payload.documentId, additionalPayload: data }));
        yield put(
            clearCache({
                cacheType: CACHE_TYPE.COMMENTS,
                key: `?project=${data._id}`,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateCommentFail(error));
    }
}

function* deleteComment({ payload }: { payload: IGenericDocumentModifier }): any {
    const url = `${commentUrl}/${payload.documentId}`;
    try {
        yield call(api, url, httpRequest.DELETE, {}, 2, 2000);
        yield put(deleteCommentSuccess(payload));
        yield put(
            clearCache({
                cacheType: CACHE_TYPE.COMMENTS,
                key: `?project=${payload?.payload?.project._id}`,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteCommentFail(error));
    }
}

interface TaskAction extends Action, IGetComments {
    payload: any;
}

function* leaveACommentWatcher() {
    yield takeLatest<any>(types.LEAVE_COMMENT, leaveAComment);
}

function* updateCommentWatcher() {
    yield takeLatest<any>(types.UPDATE_COMMENT, updateComment);
}

function* deleteCommentWatcher() {
    yield takeLatest<any>(types.DELETE_COMMENT, deleteComment);
}

function* likeCommentWatcher() {
    yield takeLatest<any>(types.LIKE_COMMENT, likeAComment);
}

function* deleteCommentLikeWatcher() {
    yield takeLatest<any>(types.DELETE_COMMENT_LIKE, deleteCommentLike);
}

function* getCommentsWatcher() {
    yield takeLatest<TaskAction>(types.GET_COMMENTS, getCommentsAction);
}

export default function* commentSaga() {
    yield all([
        deleteCommentWatcher(),
        leaveACommentWatcher(),
        likeCommentWatcher(),
        updateCommentWatcher(),
        deleteCommentLikeWatcher(),
        getCommentsWatcher(),
    ]);
}
